import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useState} from "react";
import type {MathInlineElement} from "slate";
import {ReactEditor, useSlate, type RenderElementProps} from "slate-react";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {Popover, PopoverContentCore} from "@khanacademy/wonder-blocks-popover";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {MathJax} from "@khan/component-tex";
import {isKhanmigoEditing} from "@khan/guide-math-editor";

import {shouldHighlightNodePath} from "../helpers/decoration-helpers.ts";
import {updateInlineMathNode} from "../helpers/math-helpers.ts";
import {isSelectionWithinNode} from "../helpers/node-helpers.ts";

const StyledSpan = addStyle("span");

export default function MathInlineNode(
    props: RenderElementProps,
): React.ReactElement {
    const {element, attributes, children} = props;

    const editor = useSlate();
    const math = element as MathInlineElement;
    const [editedTeX, setEditedTeX] = useState(math.tex || "");

    const path = ReactEditor.findPath(editor, element);
    const selectionWithin = isSelectionWithinNode(editor, element);
    const showKhanmigoHighlight = shouldHighlightNodePath(editor, path);

    useEffect(() => {
        updateInlineMathNode(editor, editedTeX);
    }, [editor, editedTeX]);

    // When rendering void elements, Slate needs to put the ref on a
    // DOM node, hence the <span> wrapper. Also, we need to render the
    // children somewhere within the void, even though they are empty:
    // https://github.com/ianstormtaylor/slate/issues/3930
    const span = (
        <StyledSpan
            {...attributes}
            style={[
                styles.wrapper,
                selectionWithin && styles.focused,
                showKhanmigoHighlight && styles.selected,
            ]}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error
            tabIndex={0}
            data-math-content={math.tex || ""}
            contentEditable={false}
        >
            {children}
            <MathJax style={styles.math} contextForAnalytics="ai-guide" noLog>
                {math.tex || ""}
            </MathJax>
        </StyledSpan>
    );

    if (!selectionWithin || isKhanmigoEditing(editor)) {
        // If selection/cursor isn't focused on this node, we just render the
        // TeX from above to skip the unnecessary rendering of the closed
        // popover.
        return span;
    }

    return (
        <Popover
            opened
            dismissEnabled
            content={
                <PopoverContentCore style={styles.popover}>
                    <TextField
                        id="coeditor-inline-math-tex-editor"
                        value={editedTeX}
                        onChange={setEditedTeX}
                        style={styles.textField}
                        aria-label={t`math expression editor`}
                    />
                </PopoverContentCore>
            }
        >
            {span}
        </Popover>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        cursor: "pointer",
    },
    focused: {
        outline: `1px solid ${color.blue}`,
        outlineOffset: spacing.xxxxSmall_2,
        borderRadius: spacing.xxxSmall_4,
    },
    selected: {
        backgroundColor: color.fadedPurple8,
    },
    math: {
        display: "inline-block",
        color: "inherit",
        lineHeight: "1em",
    },
    popover: {
        padding: spacing.xSmall_8,
    },
    textField: {
        padding: 0,
        textAlign: "center",
    },
});
